import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface HireNavbarProps {
  scrolled: boolean;
}

export default function HireNavbar({ scrolled }: HireNavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [partyDropdownOpen, setPartyDropdownOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
      setPartyDropdownOpen(false);
    }
  };

  const navTextClass = scrolled
    ? 'text-stone-600 hover:text-amber-600'
    : 'text-white/90 hover:text-amber-200';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/98 backdrop-blur-lg shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="w-full px-6 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo with FMR tooltip */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer group relative">
            <img
              src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
              alt="House of FMR"
              className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="hidden sm:block">
              <p className={`font-display text-xl tracking-wide ${scrolled ? 'text-stone-900' : 'text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300'}`}>
                House of FMR
              </p>
              <p className="text-amber-400/80 text-[10px] tracking-[0.25em] uppercase font-medium">
                Flair • Mixology • Rentals
              </p>
            </div>
            {/* FMR Tooltip */}
            <div className="absolute top-full left-0 mt-2 px-3 py-1.5 bg-stone-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Flair • Mixology • Rentals
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-7">
            <Link to="/" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${navTextClass}`}>
              Home
            </Link>
            <button onClick={() => scrollToSection('portable-bars')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${navTextClass}`}>
              Portable Bars
            </button>
            <button onClick={() => scrollToSection('bar-mats')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${navTextClass}`}>
              Bar Accessories
            </button>

            {/* Party Rentals Dropdown */}
            <div className="relative" onMouseEnter={() => setPartyDropdownOpen(true)} onMouseLeave={() => setPartyDropdownOpen(false)}>
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${navTextClass}`}>
                Party Rentals
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${partyDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {partyDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-52 bg-white rounded-xl shadow-xl border border-stone-100 py-2 z-50">
                  {[
                    { id: 'party-glassware', label: 'Glasses & Glassware', icon: 'ri-goblet-line' },
                    { id: 'party-tables', label: 'Tables & Linens', icon: 'ri-table-line' },
                    { id: 'display-boards', label: 'Display Boards', icon: 'ri-layout-column-line' },
                    { id: 'party-helpers', label: 'Party Helpers', icon: 'ri-service-line' },
                    { id: 'consumables', label: 'Consumables', icon: 'ri-snowflake-line' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-stone-600 hover:text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${item.icon} text-amber-500 text-base`}></i>
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => scrollToSection('booking-calendar')} className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${navTextClass}`}>
              Check Availability
            </button>
            <Link to="/services" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${navTextClass}`}>
              Services & Pricing
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:0411164144" className={`flex items-center gap-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${navTextClass}`}>
              <i className="ri-phone-line"></i>
              0411 164 144
            </a>
            <button
              onClick={() => scrollToSection('hire-inquiry')}
              className="px-6 py-2.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-900 text-sm font-semibold rounded-full hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/30"
            >
              Request Quote
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-all ${
              scrolled ? 'bg-stone-100 text-stone-900' : 'bg-white/10 text-white border border-amber-300/20'
            }`}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {/* Mobile Menu - Thumb-friendly */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 bg-white rounded-2xl p-5 shadow-2xl border border-amber-100">
            <div className="space-y-1">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 w-full px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer">
                <i className="ri-home-line text-base"></i>Home
              </Link>
              {[
                { id: 'portable-bars', label: 'Portable Bars', icon: 'ri-store-2-line' },
                { id: 'bar-mats', label: 'Bar Accessories', icon: 'ri-layout-grid-line' },
                { id: 'party-glassware', label: 'Glasses & Glassware', icon: 'ri-goblet-line' },
                { id: 'party-tables', label: 'Tables & Linens', icon: 'ri-table-line' },
                { id: 'display-boards', label: 'Display Boards', icon: 'ri-layout-column-line' },
                { id: 'party-helpers', label: 'Party Helpers', icon: 'ri-service-line' },
                { id: 'consumables', label: 'Consumables', icon: 'ri-snowflake-line' },
                { id: 'booking-calendar', label: 'Check Availability', icon: 'ri-calendar-line' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="flex items-center gap-3 w-full px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={`${item.icon} text-base`}></i>{item.label}
                </button>
              ))}
              <Link to="/services" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 w-full px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer">
                <i className="ri-service-line text-base"></i>Services & Pricing
              </Link>
              <div className="h-px bg-stone-200 my-2"></div>
              <button
                onClick={() => scrollToSection('hire-inquiry')}
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-900 text-sm font-semibold rounded-lg hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/30"
              >
                <i className="ri-mail-send-line text-base"></i>Request Quote
              </button>
              <a
                href="tel:0411164144"
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-stone-100 text-stone-700 text-sm font-semibold rounded-lg hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-base"></i>Call 0411 164 144
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}